import styled, { useTheme } from "styled-components";
import { IconPhone } from "suomifi-icons";
import type { Laite } from "../types";
import { formatAikaleima } from "../utils/format";
import { useTranslation } from "react-i18next";

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $deactivated: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.m};
  padding: ${({ theme }) => theme.spacing.s} 0;
  opacity: ${({ $deactivated }) => ($deactivated ? 0.6 : 1)};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  & > svg {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.s};
`;

const NimiText = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBold};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const StatusLabel = styled.span<{ $active: boolean }>`
  background: ${({ theme, $active }) =>
    $active ? theme.colors.successDark1 : theme.colors.depthDark1};
  color: ${({ theme }) => theme.colors.whiteBase};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
`;

const SubText = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const MetaRow = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const Mono = styled.span`
  font-family: "Courier New", monospace;
`;

export default function Laitteet({ laitteet }: { laitteet: Laite[] }) {
  const theme = useTheme();
  const { t } = useTranslation(["laitteet", "common"]);

  if (laitteet.length === 0) {
    return <Empty>{t("ei_laitteita")}</Empty>;
  }

  return (
    <List>
      {laitteet.map((laite) => {
        const deactivated = laite.status === "deaktivoitu";
        // iOS = sininen, Android = vihreä — yksinkertainen visuaalinen erottelu
        // ilman kulttuurisesti latautuneita brändi-ikoneja.
        const color =
          laite.tyyppi === "iOS"
            ? theme.colors.highlightBase
            : theme.colors.successBase;
        return (
          <Row key={laite.id} $deactivated={deactivated}>
            <IconWrap>
              <IconPhone color={color} aria-hidden />
            </IconWrap>
            <Body>
              <TopRow>
                <NimiText>{laite.nimi}</NimiText>
                <StatusLabel $active={!deactivated}>
                  {deactivated
                    ? t("status.deaktivoitu")
                    : t("status.aktiivinen")}
                </StatusLabel>
              </TopRow>
              <SubText>
                {laite.tyyppi} · {laite.kayttojarjestelma}
                {laite.sovellusversio &&
                  ` ${t("sovellus")} ${laite.sovellusversio}`}
              </SubText>
              <MetaRow>
                {t("aktivoitu")} {formatAikaleima(laite.aktivoitu)}
              </MetaRow>
              <MetaRow>
                ID: <Mono>{laite.id}</Mono>
              </MetaRow>
            </Body>
          </Row>
        );
      })}
    </List>
  );
}
